import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { API } from "../lib/api";
import { BUSINESS, TRIP_TYPES, waLink } from "../data/packages";

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-aurora/60 focus:ring-2 focus:ring-aurora/20";

const CHANNELS = [
  {
    icon: Phone,
    title: "Call Us",
    value: `${BUSINESS.phonePrimary} · ${BUSINESS.phoneSecondary}`,
    sub: "Tap to call — we answer fast",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp",
    value: "Chat with a curator",
    sub: "Booking & payments on WhatsApp",
    href: waLink(`Hi ${BUSINESS.name}! I have a question about your trips.`),
  },
  ...(BUSINESS.email
    ? [{ icon: Mail, title: "Email", value: BUSINESS.email, sub: "Replies within 24 hours", href: `mailto:${BUSINESS.email}` }]
    : []),
  {
    icon: MapPin,
    title: "Offices",
    value: BUSINESS.cities,
    sub: "Visit us by appointment",
  },
];

export default function ContactUs() {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", tripType: TRIP_TYPES[0], message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        trip_type: form.tripType,
        message: form.message || null,
        source: "contact-page",
      });
      toast.success("Message sent! Our team will get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", tripType: TRIP_TYPES[0], message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-contact-us">
      <PageHeader
        testid="contact-header"
        eyebrow="Contact Us"
        title="Connect with our"
        accent="route curators"
        description="A question about a route, a date, a dream? Write, call or drop by — we reply fast."
      />

      <section className="mx-auto grid max-w-7xl gap-10 px-6 pb-24 md:pb-32 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-5">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <a
                href={c.href || "#"}
                target={c.href ? "_blank" : undefined}
                rel={c.href ? "noreferrer" : undefined}
                data-testid={`contact-channel-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-start gap-5 rounded-3xl border border-white/10 bg-obsidian p-6 transition-all duration-500 hover:border-aurora/40 hover:-translate-y-0.5"
              >
                <span className="rounded-2xl border border-aurora/30 bg-aurora/10 p-3 text-aurora">
                  <c.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{c.title}</span>
                  <span className="mt-1.5 block font-display text-base font-bold text-slate-50">{c.value}</span>
                  <span className="mt-1 block text-xs text-slate-400">{c.sub}</span>
                </span>
              </a>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="flex items-center gap-3 rounded-3xl border border-comet/25 bg-comet/5 p-6" data-testid="contact-hours-note">
              <Clock className="h-5 w-5 shrink-0 text-comet" />
              <p className="text-sm leading-relaxed text-slate-300">
                On an upcoming trip with us? Your host's direct number is in your booking pack — available 24/7.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-[2rem] border border-white/10 bg-obsidian/70 p-7 md:p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Send a Message</p>
            <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-slate-50 md:text-3xl">
              We usually reply within <em className="font-editorial font-medium italic text-comet">24 hours</em>
            </h2>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <input required minLength={2} data-testid="contact-name-input" className={fieldCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="contact-email-input" className={fieldCls} placeholder="Email *" value={form.email} onChange={set("email")} />
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <input required type="tel" minLength={6} data-testid="contact-phone-input" className={fieldCls} placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} />
              <select data-testid="contact-trip-type-select" className={`${fieldCls} [&>option]:bg-obsidian`} value={form.tripType} onChange={set("tripType")}>
                {TRIP_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <textarea rows={5} required data-testid="contact-message-input" className={`${fieldCls} mt-5 resize-none`} placeholder="Your message *" value={form.message} onChange={set("message")} />

            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit-btn"
              className="mt-8 flex items-center justify-center gap-2 rounded-xl bg-aurora px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,245,212,0.4)] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
